/**
 * Форматирует длительность в секундах:
 *  – для интервалов < 1 ч показывает «X мин Y с»;
 *  – для интервалов ≥ 1 ч показывает «X ч Y мин».
 *
 * @param startedAt  Начало звонка
 * @param endedAt  Конец звонка
 * @returns Строка вида «15 мин 12 с» или «1 ч 14 мин»
 */
export const formatDuration = (startedAt: string, endedAt: string): string => {
	const startMs = Date.parse(startedAt);
	const endMs = endedAt ? Date.parse(endedAt) : Date.now();

	// защита от кривых данных: если end < start — выводим «0 с»
	const totalSeconds = Math.max(0, Math.floor((endMs - startMs) / 1000));

	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;

	if (hours > 0) {
		// Пример: 1 ч 14 мин
		return `${hours} ч ${minutes} мин`;
	}

	// Пример: 15 мин 12 с (если totalSeconds < 60 то minutes будет 0)
	return `${minutes} мин ${seconds} с`;
};

/**
 * Возвращает время в формате «HH:MM:SS».
 *
 * - 0    → "00:00:00"
 * - 65   → "00:01:05"
 * - 3661 → "01:01:01"
 *
 * @param {number} totalSeconds Полное количество секунд.
 * @returns {string} Строка вида "HH:MM:SS".
 */
export const formatHHMMSS = (totalSeconds: number): string => {
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;

	return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

/**
 * Форматирует дату в зависимости от текущей даты и переданной даты.
 *
 * @param dateString - Дата в формате строки (например, "2024-07-07T16:40:00").
 * @param short - Флаг для короткой даты
 * @returns Отформатированная дата.
 */
export const getFormatDate = (dateString: string, short = false): string => {
	const inputDate = new Date(dateString);
	const now = new Date();
	const baseTime = `${pad(inputDate.getHours())}:${pad(inputDate.getMinutes())}`;

	// Проверяем, является ли дата сегодняшним днем
	if (inputDate.toDateString() === now.toDateString()) {
		return short ? baseTime : 'Сегодня';
	}

	// Проверяем, является ли дата вчерашним днем
	const yesterday = new Date();
	yesterday.setDate(now.getDate() - 1);
	if (inputDate.toDateString() === yesterday.toDateString()) {
		return 'Вчера';
	}

	// Проверяем, находится ли дата в текущей неделе
	const startOfWeek = new Date(now);
	startOfWeek.setDate(now.getDate() - now.getDay() + 1); // Начало недели (понедельник)
	const endOfWeek = new Date(startOfWeek);
	endOfWeek.setDate(startOfWeek.getDate() + 6); // Конец недели (воскресенье)

	if (inputDate >= startOfWeek && inputDate <= endOfWeek) {
		const shortDaysOfWeek = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
		const fullDaysOfWeek = [
			'Воскресенье',
			'Понедельник',
			'Вторник',
			'Среда',
			'Четверг',
			'Пятница',
			'Суббота',
		];
		const daysOfWeek = short ? shortDaysOfWeek : fullDaysOfWeek;

		return daysOfWeek[inputDate.getDay()];
	}

	// Проверяем, находится ли дата в текущем году
	const baseDate = `${pad(inputDate.getDate())}.${pad(inputDate.getMonth() + 1)}`;
	if (inputDate.getFullYear() === now.getFullYear()) {
		return short ? baseDate : `${baseDate} в ${baseTime}`;
	}

	// Предыдущие годы
	const fullDate = `${baseDate}.${inputDate.getFullYear()}`;
	return short ? fullDate : `${fullDate} в ${baseTime}`;
};

/**
 * Добавляет ноль перед числом, если оно меньше 10.
 *
 * @param number_ - Число для форматирования.
 * @returns Отформатированное число.
 */
export const pad = (number_: number): string => String(number_).padStart(2, '0');
